import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import "./EditClients.css";

const EditClients = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);

  const [meta, setMeta] = useState({
    enrollment_status: "",
    machine_uuid: "",
  });

  const [form, setForm] = useState({
    customer_name: "",
    address: "",
    phone: "",
    mobile: "",
    email: "",
    network_name: "",
    network_location: "",
    notes: "",
    lan_ip: "",
    public_ip: "",
    license_status: "trial",
    license_type: "monthly",
    channel_limit: 50,
  });

  useEffect(() => {
    const fetchClientDetails = async () => {
      try {
        setFetchLoading(true);
        const res = await axios.get(`/api/clients/details/${id}`);

        if (res.data?.status === "success" && res.data?.data) {
          const client = res.data.data;
          setForm({
            customer_name: client.customer_name || "",
            address: client.address || "",
            phone: client.phone || "",
            mobile: client.mobile || "",
            email: client.email || "",
            network_name: client.network_name || "",
            network_location: client.network_location || "",
            notes: client.notes || "",
            lan_ip: client.lan_ip || "",
            public_ip: client.public_ip || "",
            license_status: client.license_status || "trial",
            license_type: client.license_type || "monthly",
            channel_limit: client.channel_limit ?? 50,
          });
          setMeta({
            enrollment_status: client.enrollment_status || "pending",
            machine_uuid: client.machine_uuid || "",
          });
        } else {
          alert("⚠️ Failed to retrieve client payload parameters.");
          navigate("/manage-clients");
        }
      } catch (err) {
        console.error("Error fetching client profile:", err);
        alert(err.response?.data?.message || "Internal network failure while sync client meta.");
        navigate("/manage-clients");
      } finally {
        setFetchLoading(false);
      }
    };

    if (id) fetchClientDetails();
  }, [id, navigate]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!form.customer_name || !form.network_name) {
      alert("⚠️ Customer Name এবং Network Name অবশ্যই দিতে হবে!");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.put(`/api/clients/update/${id}`, form);

      if (res.data?.status === "success") {
        alert(res.data.message || "Client Profile Updated! 🚀");
        navigate("/manage-clients");
      } else {
        alert(res.data?.message || "Failed to commit client update.");
      }
    } catch (err) {
      console.error("Update Error:", err);
      alert(err.response?.data?.message || "Failed to finalize database update transactions.");
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh", flexDirection: "column", gap: "12px" }}>
        <div style={{ width: "40px", height: "40px", border: "4px solid #f3f3f3", borderTop: "4px solid #0f172a", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
        <p style={{ color: "#64748b", fontSize: "14px", fontWeight: "600" }}>Fetching client profile...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="add-client-page-container" style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}>

      <div className="add-client-header-wrapper" style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "32px" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "24px", color: "#1e293b", fontWeight: "800" }}>Modify Client Profile</h2>
          <p style={{ margin: 0, color: "#64748b", fontSize: "14px" }}>Update customer contact, network details and license policy.</p>
        </div>
      </div>

      <div className="add-client-form-card" style={{ background: "#fff", padding: "24px", borderRadius: "12px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0" }}>

        {/* ENROLLMENT STATUS BADGE */}
        <div style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px 14px", marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
          <div style={{ fontSize: "13px" }}>
            <span style={{ color: "#64748b" }}>Enrollment: </span>
            <span style={{ padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", background: meta.enrollment_status === "completed" ? "#dcfce7" : "#fef9c3", color: meta.enrollment_status === "completed" ? "#15803d" : "#a16207" }}>
              {meta.enrollment_status || "pending"}
            </span>
          </div>
          <div style={{ fontSize: "13px" }}>
            <span style={{ color: "#64748b" }}>Machine UUID: </span>
            <code style={{ fontFamily: "monospace", color: meta.machine_uuid ? "#2563eb" : "#94a3b8", fontSize: "12px" }}>
              {meta.machine_uuid || "Pending enrollment..."}
            </code>
          </div>
        </div>

        {/* SECTION 1 */}
        <div className="form-section">
          <h3 style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: "8px", color: "#0f172a" }}>1. Client Information</h3>
          <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" }}>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>Customer Name *</label>
              <input name="customer_name" value={form.customer_name} onChange={handleChange} className="form-control-input" required />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>Contact Mobile</label>
              <input name="phone" value={form.phone} onChange={handleChange} className="form-control-input" />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>Email Address</label>
              <input name="email" value={form.email} onChange={handleChange} className="form-control-input" />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>Physical Address</label>
              <input name="address" value={form.address} onChange={handleChange} className="form-control-input" />
            </div>
          </div>
        </div>

        {/* SECTION 2 */}
        <div className="form-section">
          <h3 style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: "8px", color: "#0f172a" }}>2. Network & ISP Details</h3>
          <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" }}>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>Network/ISP Name *</label>
              <input name="network_name" value={form.network_name} onChange={handleChange} className="form-control-input" required />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>Network Location Area</label>
              <input name="network_location" value={form.network_location} onChange={handleChange} className="form-control-input" />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>LAN IP</label>
              <input name="lan_ip" value={form.lan_ip} onChange={handleChange} className="form-control-input" style={{ fontFamily: "monospace" }} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>Public IP</label>
              <input name="public_ip" value={form.public_ip} onChange={handleChange} className="form-control-input" style={{ fontFamily: "monospace" }} />
            </div>
          </div>
        </div>

        {/* SECTION 3 */}
        <div className="form-section">
          <h3 style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: "8px", color: "#0f172a" }}>3. License Policy</h3>
          <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginBottom: "24px" }}>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>License Status</label>
              <select name="license_status" value={form.license_status} onChange={handleChange} className="form-control-input" style={{ width: "100%", height: "40px" }}>
                <option value="trial">Trial</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>License Type</label>
              <select name="license_type" value={form.license_type} onChange={handleChange} className="form-control-input" style={{ width: "100%", height: "40px" }}>
                <option value="monthly">Monthly</option>
                <option value="lifetime">Lifetime</option>
                <option value="others">Others</option>
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>Channel Limit</label>
              <input type="number" name="channel_limit" value={form.channel_limit} onChange={handleChange} className="form-control-input" />
            </div>
            <div style={{ gridColumn: "span 3" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>Internal Notes</label>
              <textarea name="notes" value={form.notes} onChange={handleChange} className="form-control-input" style={{ minHeight: "40px", width: "100%" }} />
            </div>
          </div>
          <p style={{ fontSize: "11px", color: "#94a3b8", margin: 0 }}>
            নোট: expiry date / renew করতে Manage License → Renew/Edit ব্যবহার করুন।
          </p>
        </div>

        {/* ACTIONS */}
        <div className="form-actions" style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
          <button type="button" onClick={() => navigate("/manage-clients")} style={{ padding: "10px 20px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer", fontWeight: "bold" }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading} className="btn-submit-client" style={{ padding: "10px 24px", borderRadius: "6px", border: "none", background: loading ? "#94a3b8" : "#0f172a", color: "#fff", fontWeight: "bold", cursor: loading ? "not-allowed" : "pointer" }}>
            {loading ? "Saving..." : "💾 Save Changes"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default EditClients;